import { Product } from "@/types";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { useState } from "react";

interface ProductCardProps {
  product: Product;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const { addToCart } = useCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleAddToCart = async (e: React.MouseEvent) => {
    // Stop event propagation to prevent triggering parent hover effects
    e.stopPropagation();

    try {
      setIsAddingToCart(true);
      await addToCart(product);
      // Toast is handled in CartContext
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Get final price (use f_price or special_price, fallback to price)
  const finalPrice = product.f_price || product.special_price || product.price;

  // Get brand name (use brand_label or brand_name)
  const brandName = product.brand_label || product.brand_name || "";

  // Get product image with proper fallback handling
  const getImageSrc = () => {
    // If base_image exists and is not null, construct full URL
    if (product.base_image && product.base_image !== null) {
      const baseUrl = product.s3_base_url || product.media_base_url || "";
      return `${baseUrl}${product.base_image}`;
    }

    // If thumb_image exists, use it
    if (product.thumb_image && product.thumb_image !== null) {
      const baseUrl = product.s3_base_url || product.media_base_url || "";
      return `${baseUrl}${product.thumb_image}`;
    }

    // Fallback to default_image with proper base URL
    if (product.default_image) {
      // If default_image is a relative path, prepend https://sparts.in:8443
      if (
        product.default_image.startsWith("vendor/") ||
        product.default_image.startsWith("product/")
      ) {
        return `https://sparts.in:8443/${product.default_image}`;
      }
      return product.default_image;
    }

    // Ultimate fallback - placeholder image
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
  };

  const imageSrc = getImageSrc();


  return (
    <Card className="p-4 flex flex-col hover:shadow-md transition-shadow h-full relative">
      {/* Floating Discount Label in Top Right Corner */}
      {product.selling_price_discount > 0 && (
        <span
          className="absolute top-2 right-2 bg-red-600 text-white text-xs font-semibold py-1 px-3 rounded-lg shadow"
          style={{ backgroundColor: '#f3465f', zIndex: 10 }}
        >
          {product.selling_price_discount}% OFF
        </span>
      )}
      {/* Product Image */}
      <div className="w-full aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden">
        <img
          src={imageSrc}
          alt={product.name}
          className="w-full h-full object-contain p-2"
          onError={(e) => {
            // Try default_image first
            if (
              e.currentTarget.src !== product.default_image &&
              product.default_image
            ) {
              e.currentTarget.src =
                product.default_image.startsWith("vendor/") ||
                product.default_image.startsWith("product/")
                  ? `https://sparts.in:8443/${product.default_image}`
                  : product.default_image;
            } else {
              // Final fallback to SVG placeholder
              e.currentTarget.src =
                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="24" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
            }
          }}
        />
      </div>

      {/* Content - grows to push button to bottom */}
      <div className="flex flex-col flex-1 items-center text-center gap-1">
        {/* Brand */}
        <p className="text-xs text-muted-foreground uppercase tracking-wide">
          {brandName}
        </p>

        {/* Product Name */}
        <h3 className="text-sm font-medium line-clamp-2 min-h-[2.5rem] w-full px-1">
          {product.name}
        </h3>
        </div>
        {/* Original Price (if different) */}
        <div className="flex flex-col md:flex-row items-center w-full mt-2 mb-1 justify-between">
          <div className="flex flex-col w-[100%] md:w-[50%] items-center">
        {product.price > finalPrice && (
          <p className="text-xs text-muted-foreground line-through">
            ₹{product.price.toLocaleString()}
          </p>
        )}
        {/* Price Section */}
        <div className="flex flex-col items-center gap-0.5 mt-auto mb-1">
          <p className="text-lg font-bold text-foreground">
            ₹{finalPrice.toLocaleString()}
          </p>

          <p className="text-xs text-muted-foreground mb-2">Exc. of GST</p>

          {/* Discount Badge */}
        </div>
</div>
        {/* GST Info */}
    

      {/* Add Button - Always at bottom */}
      <div className="w-[100%] md:w-[50%] flex justify-end">
      <Button
        onClick={handleAddToCart}
        size="sm"
        className="w-[100%] mt-auto"
        disabled={isAddingToCart}
      >
        {isAddingToCart ? "Adding..." : "Add"}
      </Button>
      </div>
      </div>
    </Card>
  );
};
